import { flatten } from 'lodash'
import { FetchFn } from '@wix/thunderbolt-symbols'
import { MetricsReporter } from '@wix/thunderbolt-becky-types'
import { CssPresets, FetchPresetsFn, PresetData, PresetsDataList, WidgetsToPresetToCss } from './types'
import { BeckyModel, BeckyRequestPayload } from '@wix/thunderbolt-becky-root'
import { createPresetsSiteAssetsClient } from './presetsSiteAssetsClient'

export const createFetchMemoization = (payload: BeckyRequestPayload, beckyModel: BeckyModel, fetchFn: FetchFn) => {
	const fetchPromises: Map<string, Promise<CssPresets>> = new Map()
	const memoizedFetch: FetchPresetsFn = (appDefinitionId: string, widgetId: string) => {
		const cacheKey = `${appDefinitionId}:${widgetId}`
		if (!fetchPromises.has(cacheKey)) {
			const fetchPresetsResponse = createPresetsSiteAssetsClient(payload, beckyModel, fetchFn, {
				appDefinitionId,
				widgetId,
			})
			fetchPromises.set(cacheKey, fetchPresetsResponse)
		}
		return fetchPromises.get(cacheKey) as Promise<CssPresets>
	}
	return memoizedFetch
}

export const fetchPresets = async (
	presets: PresetsDataList,
	fetchFn: FetchFn,
	metricsReporter: MetricsReporter,
	payload: BeckyRequestPayload,
	beckyModel: BeckyModel
): Promise<WidgetsToPresetToCss> => {
	const memoizedPresetsFetch = createFetchMemoization(payload, beckyModel, fetchFn)

	return fetchPresetsInner(presets, memoizedPresetsFetch, metricsReporter)
}

export const fetchPresetsInner = async (
	presets: PresetsDataList,
	memoizedPresetsFetch: FetchPresetsFn,
	metricsReporter: MetricsReporter
) => {
	const presetsCss = await Promise.all(
		presets.map(({ appDefinitionId, widgetId, presetId }: PresetData) => {
			return memoizedPresetsFetch(appDefinitionId, widgetId)
				.then((resp) => {
					return resp.css[presetId]
				})
				.catch((e) => {
					metricsReporter.reportError(e)
					throw e
				})
				.then((presetCss) => ({ widgetId, presetId, presetCss }))
		})
	)

	return flatten(presetsCss).reduce((acc: WidgetsToPresetToCss, { widgetId, presetId, presetCss }) => {
		acc[widgetId] = acc[widgetId] || {}
		acc[widgetId][presetId] = presetCss
		return acc
	}, {})
}
